<template>
  <div class="load-placeholder" v-if="loading">
    <loading-symbol />
  </div>
  <div v-else-if="provider === null">
    <no-data-message message="provider.detail.noData" />
  </div>
  <div v-else>
    <mobile-restrict-dialog v-if="mobileOnlyDialogOpen" />
    <div class="provider-detail">
      <div class="provider-header">
        <img v-if="providerLogo" alt="Provider logo" :src="providerLogo"
          class="provider-logo" v-bind:class="align" />
        <h1><L10n :item="provider" name="name" /></h1>
      </div>
      <div v-if="provider.description" class="provider-description">
        <h2>{{ $t("provider.detail.about") }}</h2>
        <L10n :item="provider" name="description" html />
      </div>
      <provider-contact :provider="provider" />
      <div v-if="benefits && benefits.length" class="provider-discounts">
        <h2>{{ $t("provider.detail.discounts") }}</h2>
        <discount :discount="benefit" :key="benefit.name"
            v-for="benefit in benefits" />
      </div>
      <div v-if="providerMedia && providerMedia.length" class="provider-gallery">
        <h2>{{ $t("provider.detail.gallery") }}</h2>
        <image-gallery :images="providerMedia" />
      </div>
      <provider-map />
      <provider-featured-offers v-if="locations" />
      <div v-if="providerTags.length" class="provider-tags">
        <h2>{{ $t("provider.detail.tags") }}</h2>
        <tag-list :tags="providerTags" />
      </div>
    </div>
  </div>
</template>

<style scoped>
  .load-placeholder {
    margin-top: 200px;
    height: 450px;
  }

  .provider-logo {
    max-width: 130px;
    max-height: 70px;
  }

  .provider-detail {
    color: var(--secondary-color);
    font-size: 14px;
  }

  .provider-discounts {
    clear: both;
  }

  .provider-tags {
    margin-top: -16px;
  }

  h1 {
    color: var(--primary-color);
    font-size: 32px;
  }

  .provider-description {
    text-align: justify;
    margin-bottom: 16px;
  }

</style>

<style>
  .provider-detail .provider-description a:hover {
    color: var(--primary-color);
  }

  h2 {
    color: var(--secondary-color);
    font-size: 26px;
    margin-top: 24px;
    margin-bottom: 8px;
  }
</style>

<script>
import { mapState, mapActions } from 'vuex';
import NoDataMessage from '@/components/NoDataMessage.vue';
import LoadingSymbol from '@/components/LoadingSymbol.vue';
import MobileRestrictDialog from '@/components/discount/MobileRestrictDialog.vue';
import ImageGallery from '../components/ImageGallery.vue';
import Discount from '../components/discount/Discount.vue';
import ProviderFeaturedOffers from '../components/provider/ProviderFeaturedOffers.vue';
import TagList from '../components/tag/TagList.vue';
import ProviderMap from '../components/map/ProviderMap.vue';
import ProviderContact from '../components/provider/ProviderContact.vue';
import L10n from '../components/L10n.vue';

export default {
  name: 'provider-detail',
  data() {
    return {
      align: this.$store.getters.getConfig.align,
    };
  },
  computed: {
    providerTags() {
      const tags = [];
      this.benefits.map((benefit) => benefit.tags).forEach((benefitTags) => {
        if (benefitTags) {
          benefitTags.forEach((tag) => {
            if (tags.indexOf(tag.name) === -1) {
              tags.push(tag.name);
            }
          });
        }
      });
      return tags;
    },
    providerMedia() {
      let providerMedia = [];
      this.benefits.map((benefit) => benefit.media).forEach((benefitMedia) => {
        // This is a workaround because of duplicate images in gallery. See #40307.
        // Because providers added same image to all provider's benefits.
        // The workaround chooses the benefit with most images and his images are used.
        if (benefitMedia && benefitMedia.length > providerMedia.length) {
          // eslint-disable-next-line arrow-body-style
          providerMedia = benefitMedia.map((media) => {
            return { src: media.url };
          });
        }
      });
      return providerMedia;
    },
    providerLogo() {
      return this.provider.logoUrl ? this.provider.logoUrl : undefined;
    },
    ...mapState({
      provider: (state) => state.provider.detail.provider,
      benefits: (state) => state.provider.detail.benefits,
      locations: (state) => state.location.list,
      currentRoute: (state) => state.router.route,
      featuredProviders: (state) => state.provider.detail.featuredProviders,
      mobileOnlyDialogOpen: (state) => state.benefit.mobileOnlyDialogOpen,
      loading: (state) => state.provider.detail.provider === undefined
          || state.provider.detail.benefits === undefined || state.country.list === undefined,
    }),
  },
  watch: {
    benefits(benefits) {
      // We want to open voucher dialog using redeemOffer param after the provider benefits
      // are fetched. So we can do check if benefit with this ID exists.
      if (benefits) {
        this.handleRedeemOffer();
        this.handleRedirectOffer();
      }
    },
  },
  methods: {
    ...mapActions({
      fetchProvider: 'fetchProvider',
      resetProviderDetail: 'resetProviderDetail',
      openVoucherDialog: 'openVoucherDialog',
      openRedirectDialog: 'openRedirectDialog',
      fetchCountries: 'fetchCountries',
    }),
    handleRedeemOffer() {
      if (this.currentRoute?.params?.redeemOffer) {
        const benefit = this.benefits.filter(
          (discount) => discount.benefitId === this.currentRoute.params.redeemOffer,
        );
        if (benefit[0]) {
          if (benefit[0].mobileOnly) {
            this.openMobileOnlyDialog();
            return;
          }
          this.openVoucherDialog(this.currentRoute.params.redeemOffer);
        }
      }
    },
    handleRedirectOffer() {
      if (this.currentRoute?.params?.redirectOffer) {
        const benefit = this.benefits.filter(
          (discount) => discount.benefitId === this.currentRoute.params.redirectOffer,
        );
        if (benefit[0]) {
          if (benefit[0].mobileOnly) {
            this.openMobileOnlyDialog();
            return;
          }
          this.openRedirectDialog(this.currentRoute.params.redirectOffer);
        }
      }
    },
  },
  components: {
    ImageGallery,
    Discount,
    TagList,
    ProviderMap,
    LoadingSymbol,
    NoDataMessage,
    ProviderFeaturedOffers,
    L10n,
    ProviderContact,
    MobileRestrictDialog,
  },
  created() {
    this.fetchProvider(this.currentRoute.params.providerId);
    this.fetchCountries();
  },
  beforeDestroy() {
    this.resetProviderDetail();
  },
};
</script>
